import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { LocalStorageService } from '@app/core/service/local-storage.service';
import { FcmService } from '@app/fcm/fcm.service';
import { App } from '@capacitor/app';
import { IfVisible } from '@root/src/client/static_modules/@rosskevin/ifvisible';
import { BehaviorSubject } from 'rxjs';
import { ToastService } from './toast.service';

const WHITELISTED_IDLE_PAGES = ['/login', '/pin', '/404'];
const DEFAULT_PASSCODE_TIMEOUT = 60;
const IFVISIBLE_KEY = 'ifvisible_passcodeTimeout';

@Injectable()
export class PasscodeTimeoutService {
  private _timeToIdle$ = new BehaviorSubject(DEFAULT_PASSCODE_TIMEOUT);
  private _timeToIdleLeft$ = new BehaviorSubject(this._timeToIdle$.value);
  private _lastActivityTime: Date = new Date();

  public timeToIdle$ = this._timeToIdle$.asObservable();
  public timeToIdleLeft$ = this._timeToIdleLeft$.asObservable();

  public constructor(
    private readonly _authenticationService: AuthenticationService,
    private readonly _fcmService: FcmService,
    private readonly _localStorageService: LocalStorageService,
    private readonly _router: Router,
    private readonly _toastService: ToastService,
    private readonly _ngZone: NgZone
  ) {
    (window as any)[IFVISIBLE_KEY] = new IfVisible(window, document);

    this._addActivityEventListeners();

    (window as any)[IFVISIBLE_KEY].on('idle', () => {
      this._ngZone.run(() => {
        const multiUserMode =
          !!this._localStorageService.getItem('multiUserMode');

        if (WHITELISTED_IDLE_PAGES.includes(this._router.url)) {
          this._resetAllTimers();
          return;
        }

        if (multiUserMode) {
          this._resetAllTimers();
          this._authenticationService.logout({ navigateTo: 'pin' });
          return;
        }

        this._timeToIdleLeft$.next(0);
      });
    });

    (window as any)[IFVISIBLE_KEY].on('wakeup', () => {
      this._ngZone.run(() => {
        if (WHITELISTED_IDLE_PAGES.includes(this._router.url)) {
          this._resetAllTimers();
          return;
        }

        const multiUserMode =
          !!this._localStorageService.getItem('multiUserMode');

        if (multiUserMode) {
          this._checkTimeoutAndLogout();
          this._lastActivityTime = new Date();
        }

        this._timeToIdleLeft$.next(this._timeToIdle$.value);
      });
    });

    (window as any)[IFVISIBLE_KEY].onEvery(2, () => {
      this._ngZone.run(() => {
        if (WHITELISTED_IDLE_PAGES.includes(this._router.url)) {
          this._resetAllTimers();
          return;
        }

        const multiUserMode =
          !!this._localStorageService.getItem('multiUserMode');

        const now = new Date();
        const elapsedIdleTime =
          (now.getTime() - this._lastActivityTime.getTime()) / 1000;
        const timeoutSeconds = this._timeToIdle$.value;
        const remaining = Math.max(0, timeoutSeconds - elapsedIdleTime);

        if (multiUserMode) {
          this._checkTimeoutAndLogout();
        }

        this._timeToIdleLeft$.next(remaining);
      });
    });

    App.addListener('resume', () => {
      this._ngZone.run(() => {
        if (WHITELISTED_IDLE_PAGES.includes(this._router.url)) {
          this._resetAllTimers();
          return;
        }

        const multiUserMode =
          !!this._localStorageService.getItem('multiUserMode');

        if (multiUserMode) {
          this._checkTimeoutAndLogout();
        }

        const now = new Date();
        const elapsedIdleTime =
          (now.getTime() - this._lastActivityTime.getTime()) / 1000;
        const timeoutSeconds = this._timeToIdle$.value;
        const remaining = Math.max(0, timeoutSeconds - elapsedIdleTime);
        this._timeToIdleLeft$.next(remaining);
      });
    });
  }

  private _checkTimeoutAndLogout() {
    const multiUserMode = !!this._localStorageService.getItem('multiUserMode');

    if (WHITELISTED_IDLE_PAGES.includes(this._router.url)) {
      this._resetAllTimers();
      return;
    }

    if (!multiUserMode) {
      return;
    }

    const now = new Date();
    const elapsedIdleTime =
      (now.getTime() - this._lastActivityTime.getTime()) / 1000;

    const timeoutSeconds = this._timeToIdle$.value;

    if (elapsedIdleTime >= timeoutSeconds) {
      this._resetAllTimers();
      this._authenticationService.logout({ navigateTo: 'pin' });
      return;
    }
  }

  private _addActivityEventListeners() {
    const updateLastActivity = () => {
      this._ngZone.run(() => {
        this._lastActivityTime = new Date();
        this._timeToIdleLeft$.next(this._timeToIdle$.value);
      });
    };

    document.addEventListener('touchstart', updateLastActivity);
    document.addEventListener('touchmove', updateLastActivity);
    document.addEventListener('touchend', updateLastActivity);
    document.addEventListener('click', updateLastActivity);
    document.addEventListener('keydown', updateLastActivity);
  }

  public initPasscodeTimeout() {
    let passcodeTimeout = parseFloat(
      this._localStorageService.getItem('passcodeTimeout')
    );

    if (!passcodeTimeout || isNaN(passcodeTimeout)) {
      passcodeTimeout = DEFAULT_PASSCODE_TIMEOUT;
    }

    if (passcodeTimeout) {
      this._timeToIdle$.next(Number(passcodeTimeout));
      (window as any)[IFVISIBLE_KEY].setIdleDuration(this._timeToIdle$.value);
    }

    this._resetAllTimers();
  }

  public activateMultiUserMode(
    passcodeRedirectsTo: string,
    passcodeTimeout: number
  ) {
    this._localStorageService.setItem('multiUserMode', true);
    this._localStorageService.setItem(
      'multiUserModeAuthToken',
      this._authenticationService.getAuthToken().token
    );
    this._localStorageService.setItem(
      'passcodeRedirectsTo',
      passcodeRedirectsTo
    );
    this._localStorageService.setItem('passcodeTimeout', passcodeTimeout);

    this.initPasscodeTimeout();

    this._toastService.presentToast('Multi-User Mode Activated!');

    this._authenticationService.logout({ navigateTo: 'pin' });
  }

  private _resetAllTimers() {
    this._lastActivityTime = new Date();
    this._timeToIdleLeft$.next(this._timeToIdle$.value);
  }

  public deactivateMultiUserMode() {
    this._localStorageService.clearItem('multiUserMode');
    this._localStorageService.clearItem('multiUserModeAuthToken');
    this._localStorageService.clearItem('passcodeRedirectsTo');
    this._localStorageService.clearItem('passcodeTimeout');

    this._toastService.presentToast('Multi-User Mode Deactivated!');

    this._fcmService.refreshNotificationToken();
  }
}
